import { CollapsibleSection } from 'components/Collapsible';
import { ImageGrid } from 'components/ImageGrid';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { getFileTypeFromUntrustedExtension } from 'service/file';
import { FILE_TYPES } from 'service/file/types';
import { useDispatch } from 'react-redux';
import { doFileShareDirect, doSetImagePreview } from 'store/actions';
import type { TaskDetailsResponse } from 'types/Tasks';

type TaskPhoto = TaskDetailsResponse['photos'][number];

const getPhotoFileType = (photo: TaskPhoto) => {
  const extension = photo.filename.split('.').pop() ?? '';
  return getFileTypeFromUntrustedExtension(extension) ?? FILE_TYPES.jpeg;
};

export const TaskAssignedPhotosSection: React.FC<{
  photos: TaskDetailsResponse['photos'];
  expanded: boolean;
  onToggle: () => void;
}> = ({ photos, expanded, onToggle }) => {
  const dispatch = useDispatch();
  const photosLabel = pluralise(photos.length, '{x} Photo', '{x} Photos');
  const photosById = React.useMemo(
    () => new Map(photos.map((photo) => [photo.id, photo])),
    [photos],
  );
  const photoIds = React.useMemo(
    () => photos.map((photo) => photo.id),
    [photos],
  );

  const handlePreview = React.useCallback(
    (photoId: string) => {
      const photo = photosById.get(photoId);
      if (photo) {
        dispatch(doSetImagePreview({ image: photo.largeUrl }));
      }
    },
    [dispatch, photosById],
  );

  const handleDownload = React.useCallback(
    (photoId: string) => {
      const photo = photosById.get(photoId);
      if (photo) {
        dispatch(
          doFileShareDirect({
            url: photo.fullUrl,
            title: photo.filename,
            fileType: getPhotoFileType(photo),
          }),
        );
      }
    },
    [dispatch, photosById],
  );

  return (
    <CollapsibleSection
      expanded={expanded}
      label={photosLabel}
      onToggle={onToggle}
    >
      <View style={styles.gridContainer}>
        <ImageGrid
          ids={photoIds}
          idealTileWidth={85}
          remoteUrl={(photoId) => photosById.get(photoId)!.smallUrl}
          onPreview={handlePreview}
          onDownload={handleDownload}
        />
      </View>
    </CollapsibleSection>
  );
};

const styles = StyleSheet.create({
  gridContainer: {
    backgroundColor: theme.colors.white,
    padding: 2,
    borderRadius: theme.cardBorderRadius,
    marginHorizontal: theme.padding,
  },
});
